import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { Field, RootState } from '../../../types/common.types';
import { Attribute, Programme } from '../../../types/entity.types';

import { useExternalRegistrationApi } from '../../../api/external-registration/useExternalRegistrationApi';

import { addSpinner, addSystemNotice } from '../../../slices/system/system.slice';

import { ERROR, REQUIRED_REGISTRATION_FIELDS } from '../../../utils/constants';

import ProgrammeDetails from '../../../components/screens/auth/programme-details/ProgrammeDetails';

const ProgrammeDetailsContainer = () => {
  const { programmes } = useSelector((state: RootState) => state.registration);

  const [programme, setProgramme] = useState<Programme | undefined>(undefined);
  const [fields, setFields] = useState<Field[]>([]);

  const { externalReference } = useParams();

  const [searchParams] = useSearchParams();

  const dispatch = useDispatch();

  const { findActiveProgrammes, createMember } = useExternalRegistrationApi();

  useEffect(() => {
    if (!programmes || programmes.length === 0) {
      findActiveProgrammes();
    }
  }, [findActiveProgrammes, programmes]);

  useEffect(() => {
    if (programmes && programmes.length > 0) {
      const foundProgramme = programmes.find((programme) => programme.externalReference === externalReference);
      setProgramme(foundProgramme);
    }
  }, [programmes, externalReference]);

  useEffect(() => {
    if (programme) {
      const fields = (programme.registrationRequirement?.requiredRegistrationFields as string[]).map((field): Field => {
        const attributes = field.split('**');
        return {
          label: attributes[0] as string,
          optional: attributes[1] === 'optional',
          type: attributes[2] as string,
          value: ''
        };
      });
      setFields([...REQUIRED_REGISTRATION_FIELDS, ...fields]);
    }
  }, [programme]);

  const updateField = (label: string, value: string | boolean) => {
    const copyOfFields = structuredClone(fields);

    const field = copyOfFields.find((field) => field.label === label);
    if (field) {
      field.value = value;

      setFields(copyOfFields);
    }
  };

  const showToaster = (message: string) => {
    dispatch(addSystemNotice({ type: ERROR, message }));
  };

  const getValue = (label: string) => fields.find((field) => field.label === label)?.value;

  const getItemDescription = () => {
    const memberNumber = getValue('Member Number');
    const emailAddress = getValue('Email Address');

    let description = '';
    if (memberNumber) {
      description = memberNumber as string;
    } else if (emailAddress) {
      description = emailAddress as string;
    }

    const idNumber = getValue('ID Number') as string;
    if (idNumber) {
      description = `${description} / ${idNumber}`;
    }

    return description;
  };

  const postToPayfast = (memberExternalReference: string) => {
    if (!memberExternalReference) {
      showToaster('An error occurred. Please try again.');
      return;
    }

    dispatch(addSpinner('Navigating to Payfast'));
    const form = document.createElement('form');
    const payfastURL = process.env.REACT_APP_PAYFAST_URL;

    if (!payfastURL) {
      showToaster('An error occurred');
      throw new Error('PayFast URL is undefined');
    }

    form.method = 'post';
    form.action = payfastURL;

    let returnUrl = `${process.env.REACT_APP_HOST}/register/success/${externalReference}/${memberExternalReference}`;
    let cancelUrl = `${process.env.REACT_APP_HOST}/register/${externalReference}`;
    const theme = searchParams.get('theme');
    if (theme) {
      returnUrl = `${returnUrl}?theme=${theme}`;
      cancelUrl = `${cancelUrl}?theme=${theme}`;
    }

    const fields = [
      { name: 'merchant_id', value: programme?.registrationRequirement?.payfastMerchantId as string },
      { name: 'merchant_key', value: programme?.registrationRequirement?.payfastMerchantKey as string },
      { name: 'return_url', value: returnUrl },
      { name: 'cancel_url', value: cancelUrl },
      { name: 'amount', value: programme?.registrationRequirement?.programmePrice as string },
      { name: 'item_name', value: programme?.name as string },
      {
        name: 'item_description',
        value: getItemDescription()
      },
      { name: 'custom_str1', value: programme?.registrationRequirement?.programmePrice as string }
    ];

    fields.forEach((field) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = field.name;
      input.value = field.value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const handleProceedToPaymentClick = () => {
    const attributes: Attribute[] = [];
    fields.forEach(({ label, value }) => {
      if (value && !REQUIRED_REGISTRATION_FIELDS.find((field) => field.label === label)) {
        attributes.push({
          key: label,
          value: value.toString()
        });
      }
    });

    createMember(
      {
        programmeExternalReference: externalReference!,
        member: {
          emailAddress: getValue('Email Address') as string,
          phoneNumber: getValue('Mobile Number') as string,
          firstName: getValue('First Name') as string,
          lastName: getValue('Last Name') as string,
          paid: false,
          attributes
        }
      },
      (data) => postToPayfast(data?.memberExternalReference)
    );
  };

  return (
    <ProgrammeDetails
      programme={programme}
      fields={fields}
      updateField={updateField}
      showToaster={showToaster}
      onProceedToPaymentClick={handleProceedToPaymentClick}
    />
  );
};

export default ProgrammeDetailsContainer;
